import React, { useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type ForgeDoc, type RenderFn } from '@atlassian/forge-ui-types';

import { type ComponentMap, RendererNext } from '..';
import { makeAvatar, makeAvatarStack, makeNativeUserPicker } from '../../components';

import { WebRuntime } from '../../web-runtime';
import { type UIKitRendererProps, type ViewportDimensions } from './types';
import { uncurlyUuid } from './utils';
import { useResizeAnalytics } from '../../performance-profiling/useResizeAnalytics';

const HeightAnalyticsWrapper = ({
	children,
	viewportDimensions,
}: {
	children: ReactNode;
	viewportDimensions?: ViewportDimensions;
}) => {
	const isEnabled = fg('forge-ui-performance-analytics-provider');

	const { containerRef } = useResizeAnalytics({
		isEnabled,
		viewportDimensions,
	});

	if (isEnabled) {
		return (
			<div ref={containerRef} id="forge-ui-height-observer-wrapper">
				{children}
			</div>
		);
	}

	return <>{children}</>;
};

export const UIKitRenderer = (props: UIKitRendererProps): React.JSX.Element => {
	const {
		accountId,
		bridge,
		client,
		components,
		coreData,
		contextIds,
		entryPoint,
		extension,
		extensionData,
		extensionViewData,
		getContextToken,
		mentionProvider,
		product,
		locale,
		modalExtension,
		onForgeDocUpdated,
		getThreeLOPrompt,
		customBridgeMethods,
		loadingComponent,
		onError,
		extensionPayload,
		onSuccess,
		onAppError,
		viewportDimensions,
	} = props;

	const { cloudId, workspaceId } = coreData;

	const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [shouldShowRenderer, setShouldShowRenderer] = useState(true);

	const isMacro = extension.type === 'xen:macro';

	const injectedComponentsWithKey = useMemo(() => {
		return components
			? Object.keys(components).reduce((acc, key) => {
					const Component = components[key];
					return {
						...acc,
						// A key is required to ensure React creates new component instances when forgeDoc.key changes.
						// Without this key, React reuses the same component instance across different forgeDoc updates,
						// causing hook call order violations when the component structure changes between renders.
						[key]: ((args) => <Component {...args} key={args.forgeDoc.key} />) as RenderFn,
					};
				}, {})
			: {};
	}, [components]);

	const wiredComponents = useMemo(
		() => ({
			Avatar: makeAvatar({ client }),
			AvatarStack: makeAvatarStack({ client }),
			UserPicker: makeNativeUserPicker({
				client,
				mentionProvider,
				accountId,
				...(workspaceId ? { cloudId: 'bitbucket' } : { cloudId }),
				productAttributes: workspaceId ? { workspaceIds: [uncurlyUuid(workspaceId)] } : undefined,
				productKey: product,
			}),
		}),
		[accountId, client, cloudId, mentionProvider, product, workspaceId],
	);

	const getComponents = useCallback(
		(defaults: ComponentMap) => ({
			...defaults,
			...wiredComponents,
			...(fg('forge-ui-kit-editor') ? injectedComponentsWithKey : components),
		}),
		[components, injectedComponentsWithKey, wiredComponents],
	);

	const handleError = useCallback(
		(error: Error) => {
			if (onError) {
				onError(error.message);
			}

			if (onAppError) {
				onAppError(error.message);
			}
		},
		[onError, onAppError],
	);

	useEffect(() => {
		if (onError && error) {
			onError(error.message);
		}

		if (onAppError && error) {
			onAppError(error.message);
		}
	}, [error, onError, onAppError]);

	const isUiKit1 = !(
		extension.properties.resource ||
		(entryPoint && extension.properties[entryPoint].resource)
	);

	return (
		<HeightAnalyticsWrapper viewportDimensions={isMacro ? viewportDimensions : undefined}>
			{shouldShowRenderer && (
				<RendererNext
					isNative={!isUiKit1}
					forgeDoc={forgeDoc}
					extension={extension}
					error={error?.message}
					components={getComponents}
					modalExtension={modalExtension}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					extensionType={extensionData.type}
					loading
					loadingComponent={loadingComponent}
					onError={handleError}
					client={client}
					onSuccess={onSuccess}
				/>
			)}
			{isUiKit1 ? null : (
				<WebRuntime
					accountId={accountId}
					setForgeDoc={(forgeDoc: ForgeDoc) => {
						if (forgeDoc.type !== 'MacroConfig') {
							setForgeDoc(forgeDoc);
						}
						onForgeDocUpdated?.(forgeDoc);
					}}
					setError={setError}
					apolloClient={client}
					components={getThreeLOPrompt}
					contextIds={contextIds}
					extension={extension}
					coreData={coreData}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					extensionViewData={extensionViewData}
					locale={locale}
					bridge={bridge}
					getContextToken={getContextToken}
					entryPoint={entryPoint}
					customBridgeMethods={customBridgeMethods}
					setShouldShowRenderer={setShouldShowRenderer}
				/>
			)}
		</HeightAnalyticsWrapper>
	);
};
